import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, PermissionMap, PermissionAction, PermissionModule } from '../types';
import api from '../services/api';

const FULL_ACCESS: PermissionMap = {
  leads:         { view: true, create: true, edit: true, delete: true },
  deals:         { view: true, create: true, edit: true, delete: true },
  contacts:      { view: true, create: true, edit: true, delete: true },
  organizations: { view: true, create: true, edit: true, delete: true },
  tasks:         { view: true, create: true, edit: true, delete: true },
  sim_calls:     { view: true, create: true, edit: true, delete: true },
  devices:       { view: true, create: true, edit: true, delete: true },
  campaigns:     { view: true, create: true, edit: true, delete: true },
  assignment:    { view: true, create: true, edit: true, delete: true },
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  permissions: PermissionMap;
  isAuthenticated: boolean;
  isLoading: boolean;
  can: (module: PermissionModule, action: PermissionAction) => boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, passwordConfirmation: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser]           = useState<User | null>(null);
  const [token, setToken]         = useState<string | null>(null);
  const [permissions, setPerms]   = useState<PermissionMap>(FULL_ACCESS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('crm_token');
    const storedUser  = localStorage.getItem('crm_user');
    const storedPerms = localStorage.getItem('crm_permissions');

    if (!storedToken || !storedUser) {
      setIsLoading(false);
      return;
    }

    // Optimistically hydrate from localStorage to avoid a flash of the login screen…
    setToken(storedToken);
    setUser(JSON.parse(storedUser));
    setPerms(storedPerms ? JSON.parse(storedPerms) : FULL_ACCESS);
    api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

    // …then verify the token is still valid server-side and refresh user + permissions.
    api.get('/me')
      .then(res => {
        const { user: u, permissions: p } = res.data;
        const perms = p ?? FULL_ACCESS;
        setUser(u);
        setPerms(perms);
        localStorage.setItem('crm_user', JSON.stringify(u));
        localStorage.setItem('crm_permissions', JSON.stringify(perms));
      })
      .catch(() => {
        // Token revoked / expired / invalid — clear all client state.
        setToken(null);
        setUser(null);
        setPerms(FULL_ACCESS);
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
        localStorage.removeItem('crm_permissions');
        delete api.defaults.headers.common['Authorization'];
      })
      .finally(() => setIsLoading(false));
  }, []);

  const applyAuth = (t: string, u: User, p: PermissionMap) => {
    setToken(t);
    setUser(u);
    setPerms(p);
    localStorage.setItem('crm_token', t);
    localStorage.setItem('crm_user', JSON.stringify(u));
    localStorage.setItem('crm_permissions', JSON.stringify(p));
    api.defaults.headers.common['Authorization'] = `Bearer ${t}`;
  };

  const login = async (email: string, password: string) => {
    const response = await api.post('/login', { email, password });
    const { token: t, user: u, permissions: p } = response.data;
    applyAuth(t, u, p ?? FULL_ACCESS);
  };

  const register = async (name: string, email: string, password: string, passwordConfirmation: string) => {
    const response = await api.post('/register', {
      name, email, password, password_confirmation: passwordConfirmation,
    });
    const { token: t, user: u, permissions: p } = response.data;
    applyAuth(t, u, p ?? FULL_ACCESS);
  };

  const logout = async () => {
    try { await api.post('/logout'); } catch (_) {}
    setToken(null);
    setUser(null);
    setPerms(FULL_ACCESS);
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    localStorage.removeItem('crm_permissions');
    delete api.defaults.headers.common['Authorization'];
  };

  const can = (module: PermissionModule, action: PermissionAction): boolean => {
    if (!user) return false;
    if (user.role === 'super_admin' || user.role === 'tenant_admin') return true;
    return permissions?.[module]?.[action] ?? false;
  };

  return (
    <AuthContext.Provider value={{
      user, token, permissions, isAuthenticated: !!token, isLoading, can,
      login, register, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
